import { useState, useEffect, useCallback, useMemo } from 'react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Trophy, Clock, Zap, ChevronRight, Mountain, Check, Flame } from 'lucide-react';
import { motion } from 'motion/react';
import type { ActivitySlideProps } from '../types';
import { stripHtml } from '../../../../utils/slides/stripHtml';
import { createDeterministicShuffledOrder } from '../../../../utils/slides/shuffle';

const CLIMB_COLORS = [
  { bg: 'bg-blue-500', border: 'border-blue-500', bgLight: 'bg-blue-500/10', text: 'text-blue-600' },
  { bg: 'bg-orange-500', border: 'border-orange-500', bgLight: 'bg-orange-500/10', text: 'text-orange-600' },
  { bg: 'bg-emerald-500', border: 'border-emerald-500', bgLight: 'bg-emerald-500/10', text: 'text-emerald-600' },
  { bg: 'bg-purple-500', border: 'border-purple-500', bgLight: 'bg-purple-500/10', text: 'text-purple-600' },
];

interface Question {
  question: string;
  options: string[];
  correctAnswer: number | string;
}

type SubmittedAnswerState = {
  answers: Record<number, number | string>;
  results: Record<number, boolean>;
};

export default function TimeToClimb({
  slide,
  onSubmit,
  onAnswerChange,
  selectedAnswer,
  hasSubmitted,
  shuffleSeed,
  submissions = [],
  viewMode = 'student',
  className = '',
}: ActivitySlideProps) {
  const { content } = slide;

  // Normalize questions: support both `content.questions[]` array and legacy single-question format
  const questions: Question[] = (() => {
    if (content?.questions && Array.isArray(content.questions) && content.questions.length > 0) {
      return content.questions;
    }
    // Legacy: single question/options/correctAnswer fields
    if (content?.question && content?.options) {
      return [{
        question: content.question,
        options: content.options,
        correctAnswer: content.correctAnswer ?? 0,
      }];
    }
    return [{ question: 'No question configured', options: ['A', 'B', 'C', 'D'], correctAnswer: 0 }];
  })();

  const configuredTimeLimit = Number(content?.timeLimit);
  const timeLimit = Number.isFinite(configuredTimeLimit) && configuredTimeLimit > 0
    ? configuredTimeLimit
    : 30;
  const timeLimitMode = content?.timeLimitMode === 'all_questions' ? 'all_questions' : 'per_question';
  const quizType = content?.quizType || 'multiple_choice';
  const isIdentification = quizType === 'identification';
  const totalQuestions = questions.length;
  const questionOrder = useMemo(
    () => content?.shuffleQuestions
      ? createDeterministicShuffledOrder(totalQuestions, `${shuffleSeed ?? 'default'}:${slide.id}:time-to-climb-questions`)
      : questions.map((_, index) => index),
    [content?.shuffleQuestions, questions, shuffleSeed, slide.id, totalQuestions]
  );

  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [timeLeft, setTimeLeft] = useState(timeLimit);
  const [currentSelection, setCurrentSelection] = useState<number | null>(null);
  const [identificationInput, setIdentificationInput] = useState('');
  const [showResult, setShowResult] = useState(false);
  const [answers, setAnswers] = useState<Record<number, number | string>>({});
  const [questionResults, setQuestionResults] = useState<Record<number, boolean>>({});
  const [finished, setFinished] = useState(false);

  const currentOriginalQuestionIndex = questionOrder[currentQuestionIndex] ?? 0;
  const currentQuestion = questions[currentOriginalQuestionIndex];

  const normalizeSubmittedState = useCallback((submittedAnswer: any): SubmittedAnswerState | null => {
    const submittedAnswers = submittedAnswer?.answers ?? submittedAnswer;
    if (!submittedAnswers || typeof submittedAnswers !== 'object') {
      return null;
    }

    const normalizedAnswers = submittedAnswers as Record<number, number | string>;
    const providedResults = submittedAnswer?.results;
    const normalizedResults = questions.reduce<Record<number, boolean>>((acc, question, index) => {
      if (providedResults && typeof providedResults === 'object' && typeof providedResults[index] === 'boolean') {
        acc[index] = providedResults[index];
        return acc;
      }

      if (isIdentification) {
        const student = String(normalizedAnswers[index] ?? '').trim().toLowerCase();
        const correct = String(question.correctAnswer ?? '').trim().toLowerCase();
        acc[index] = student === correct;
        return acc;
      }

      acc[index] = normalizedAnswers[index] === question.correctAnswer;
      return acc;
    }, {});

    return {
      answers: normalizedAnswers,
      results: normalizedResults,
    };
  }, [isIdentification, questions]);

  useEffect(() => {
    if (!hasSubmitted) return;

    const restoredState = normalizeSubmittedState(selectedAnswer);
    if (!restoredState) {
      return;
    }

    setAnswers(restoredState.answers);
    setQuestionResults(restoredState.results);
    setFinished(true);
    setShowResult(false);
  }, [hasSubmitted, normalizeSubmittedState, selectedAnswer]);

  // Timer countdown
  useEffect(() => {
    if (timeLeft > 0 && !showResult && !finished && !hasSubmitted) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timer);
    }
    // Auto-submit when time runs out
    if (timeLeft === 0 && !showResult && !finished && !hasSubmitted) {
      if (timeLimitMode === 'all_questions') {
        const completedAnswers = { ...answers };
        const completedResults = { ...questionResults };
        for (let index = 0; index < totalQuestions; index += 1) {
          if (completedAnswers[index] === undefined) {
            completedAnswers[index] = isIdentification ? '' : -1;
            completedResults[index] = false;
          }
        }
        setAnswers(completedAnswers);
        setQuestionResults(completedResults);
        setFinished(true);
        onAnswerChange({
          answers: completedAnswers,
          results: completedResults,
          currentQuestion: currentOriginalQuestionIndex,
        });
        onSubmit({ answers: completedAnswers, results: completedResults });
      } else if (isIdentification) {
        handleIdentificationSubmit(); // submit whatever was typed (or empty)
      } else {
        handleAnswer(-1); // -1 means time ran out, no answer
      }
    }
  }, [
    timeLeft,
    showResult,
    finished,
    hasSubmitted,
    timeLimitMode,
    answers,
    questionResults,
    totalQuestions,
    isIdentification,
    currentOriginalQuestionIndex,
    onAnswerChange,
    onSubmit,
  ]);

  const handleAnswer = useCallback((index: number) => {
    if (showResult || finished || hasSubmitted) return;

    setCurrentSelection(index);
    setShowResult(true);

    const isCorrect = index === currentQuestion.correctAnswer;
    const newAnswers = { ...answers, [currentOriginalQuestionIndex]: index };
    const newResults = { ...questionResults, [currentOriginalQuestionIndex]: isCorrect };

    setAnswers(newAnswers);
    setQuestionResults(newResults);

    // Notify parent of current answer state
    onAnswerChange({ answers: newAnswers, results: newResults, currentQuestion: currentOriginalQuestionIndex });

    // Move to next question or finish after delay
    setTimeout(() => {
      if (currentQuestionIndex < totalQuestions - 1) {
        setCurrentQuestionIndex(prev => prev + 1);
        setCurrentSelection(null);
        setShowResult(false);
        if (timeLimitMode === 'per_question') setTimeLeft(timeLimit);
      } else {
        // All questions answered - submit
        setShowResult(false);
        setFinished(true);
        onSubmit({ answers: newAnswers, results: newResults });
      }
    }, 1500);
  }, [showResult, finished, hasSubmitted, currentOriginalQuestionIndex, currentQuestionIndex, totalQuestions, answers, questionResults, currentQuestion, timeLimit, timeLimitMode, onAnswerChange, onSubmit]);

  const handleIdentificationSubmit = useCallback(() => {
    if (showResult || finished || hasSubmitted) return;

    const studentAnswer = identificationInput.trim();
    setShowResult(true);

    const correctAnswer = String(currentQuestion.correctAnswer).trim();
    const isCorrect = studentAnswer.toLowerCase() === correctAnswer.toLowerCase();
    const newAnswers = { ...answers, [currentOriginalQuestionIndex]: studentAnswer };
    const newResults = { ...questionResults, [currentOriginalQuestionIndex]: isCorrect };

    setAnswers(newAnswers);
    setQuestionResults(newResults);

    onAnswerChange({ answers: newAnswers, results: newResults, currentQuestion: currentOriginalQuestionIndex });

    setTimeout(() => {
      if (currentQuestionIndex < totalQuestions - 1) {
        setCurrentQuestionIndex(prev => prev + 1);
        setIdentificationInput('');
        setShowResult(false);
        if (timeLimitMode === 'per_question') setTimeLeft(timeLimit);
      } else {
        setShowResult(false);
        setFinished(true);
        onSubmit({ answers: newAnswers, results: newResults });
      }
    }, 1500);
  }, [showResult, finished, hasSubmitted, identificationInput, currentOriginalQuestionIndex, currentQuestionIndex, totalQuestions, answers, questionResults, currentQuestion, timeLimit, timeLimitMode, onAnswerChange, onSubmit]);

  // Calculate score
  const correctCount = Object.values(questionResults).filter(Boolean).length;

  const leaderboard = submissions
    .map((submission: any) => {
      const submittedAnswers = submission.answer?.answers ?? submission.answer;
      const providedResults = submission.answer?.results;

      const answeredCount = questions.reduce((count, _, index) => {
        const answer = submittedAnswers?.[index];
        if (answer === undefined || answer === null) {
          return count;
        }

        if (typeof answer === 'string' && answer.trim() === '') {
          return count;
        }

        if (typeof answer === 'number' && answer < 0) {
          return count;
        }

        return count + 1;
      }, 0);

      const submissionCorrectCount = questions.reduce((count, question, index) => {
        if (typeof providedResults?.[index] === 'boolean') {
          return providedResults[index] ? count + 1 : count;
        }

        const answer = submittedAnswers?.[index];
        if (answer === undefined || answer === null) {
          return count;
        }

        if (isIdentification) {
          const student = String(answer).trim().toLowerCase();
          const correct = String(question.correctAnswer ?? '').trim().toLowerCase();
          return student === correct ? count + 1 : count;
        }

        return answer === question.correctAnswer ? count + 1 : count;
      }, 0);

      return {
        id: submission.id,
        studentName: submission.student_name || 'Anonymous student',
        answeredCount,
        correctCount: submissionCorrectCount,
        percentage: totalQuestions > 0 ? Math.round((submissionCorrectCount / totalQuestions) * 100) : 0,
        submittedAt: submission.created_at ? new Date(submission.created_at).getTime() : Number.MAX_SAFE_INTEGER,
      };
    })
    .sort((left, right) => {
      if (right.correctCount !== left.correctCount) {
        return right.correctCount - left.correctCount;
      }
      if (right.answeredCount !== left.answeredCount) {
        return right.answeredCount - left.answeredCount;
      }
      return left.submittedAt - right.submittedAt;
    });

  if (viewMode === 'teacher') {
    return (
      <div className={`w-full ${className}`}>
        <div className="min-w-0 overflow-hidden bg-gradient-to-br from-amber-500/5 via-orange-500/5 to-rose-500/5 rounded-3xl p-4 sm:p-8 border border-border/40">
          <div className="text-center mb-6">
            <div className="inline-flex items-center gap-2 bg-amber-500/10 text-amber-600 px-4 py-1.5 rounded-full mb-3">
              <Flame className="w-4 h-4" />
              <span className="text-sm font-bold">Time to Climb Rankings</span>
            </div>
            <p className="text-sm text-muted-foreground">
              Live standings update automatically as students submit.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
            <div className="rounded-2xl border border-amber-200/50 bg-white/80 p-4 text-center">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-amber-600">Questions</p>
              <p className="mt-2 text-2xl font-extrabold text-foreground">{totalQuestions}</p>
            </div>
            <div className="rounded-2xl border border-emerald-200/50 bg-white/80 p-4 text-center">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-emerald-600">Responses</p>
              <p className="mt-2 text-2xl font-extrabold text-foreground">{leaderboard.length}</p>
            </div>
            <div className="rounded-2xl border border-indigo-200/50 bg-white/80 p-4 text-center">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-indigo-600">Top Score</p>
              <p className="mt-2 text-2xl font-extrabold text-foreground">{leaderboard[0]?.percentage ?? 0}%</p>
            </div>
          </div>

          {leaderboard.length > 0 ? (
            <div className="space-y-3 max-w-3xl mx-auto">
              {leaderboard.map((entry, index) => (
                <div
                  key={entry.id}
                  className={`rounded-2xl border px-4 py-3 transition-all ${
                    index === 0
                      ? 'bg-gradient-to-r from-amber-50 to-yellow-50 border-amber-200/70 shadow-sm'
                      : 'bg-white/85 border-border/70'
                  }`}
                >
                  <div className="flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:gap-4">
                    <div className="w-10 h-10 rounded-2xl bg-foreground text-background flex items-center justify-center text-sm font-extrabold">
                      #{index + 1}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-col gap-1 mb-1.5 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
                        <p className="text-base font-bold truncate">{entry.studentName}</p>
                        <div className="flex items-center gap-2 text-sm font-semibold text-muted-foreground break-words">
                          <Trophy className="w-4 h-4 text-amber-500" />
                          <span>{entry.correctCount}/{totalQuestions} correct</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="flex-1 h-2.5 bg-muted rounded-full overflow-hidden">
                          <div
                            className="h-full rounded-full bg-gradient-to-r from-amber-400 via-orange-500 to-rose-500 transition-all duration-500"
                            style={{ width: `${entry.percentage}%` }}
                          />
                        </div>
                        <span className="text-sm font-extrabold tabular-nums min-w-[3.5rem] text-right">{entry.percentage}%</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="rounded-3xl border border-dashed border-amber-200 bg-white/70 px-6 py-12 text-center max-w-2xl mx-auto">
              <Mountain className="w-14 h-14 text-amber-400 mx-auto mb-4" />
              <h3 className="text-xl font-bold mb-2">Waiting for the first climbers</h3>
              <p className="text-sm text-muted-foreground">
                Student rankings will appear here in real time once answers start coming in.
              </p>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (hasSubmitted || finished) {
    return (
      <div className={`w-full ${className}`}>
        <div className="bg-gradient-to-br from-amber-500/5 via-orange-500/5 to-rose-500/5 rounded-3xl p-6 sm:p-8 text-center border border-border/40">
          <motion.div
            initial={{ scale: 0, rotate: -20 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ type: 'spring', stiffness: 200 }}
          >
            <Trophy className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
          </motion.div>
          <h2 className="text-2xl font-extrabold mb-2">Challenge Complete!</h2>
          <p className="text-base text-muted-foreground mb-5">
            {correctCount} out of {totalQuestions} correct
          </p>
          
          {/* Score circle */}
          <div className="relative w-24 h-24 mx-auto mb-6">
            <svg className="w-24 h-24 transform -rotate-90">
              <circle cx="48" cy="48" r="42" stroke="currentColor" strokeWidth="6" fill="none" className="text-muted/50" />
              <motion.circle
                cx="48" cy="48" r="42"
                stroke="currentColor"
                strokeWidth="6"
                fill="none"
                strokeLinecap="round"
                className={correctCount === totalQuestions ? 'text-accent' : 'text-yellow-500'}
                initial={{ pathLength: 0 }}
                animate={{ pathLength: totalQuestions > 0 ? correctCount / totalQuestions : 0 }}
                transition={{ duration: 1, delay: 0.3 }}
                style={{ strokeDasharray: '264', strokeDashoffset: 0 }}
              />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              <span className={`text-2xl font-extrabold ${correctCount === totalQuestions ? 'text-accent' : 'text-yellow-500'}`}>
                {Math.round((correctCount / totalQuestions) * 100)}%
              </span>
            </div>
          </div>

          {/* Question review */}
          <div className="mt-6 space-y-3 max-w-lg mx-auto text-left">
            {questionOrder.map((originalQuestionIndex, i) => {
              const q = questions[originalQuestionIndex];
              const studentAnswer = answers[originalQuestionIndex];
              const displayStudentAnswer = isIdentification
                ? (typeof studentAnswer === 'string' && studentAnswer ? studentAnswer : 'No answer')
                : (studentAnswer !== undefined && (studentAnswer as number) >= 0 ? q.options[studentAnswer as number] : 'No answer');
              const displayCorrectAnswer = isIdentification
                ? String(q.correctAnswer)
                : q.options[q.correctAnswer as number];

              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.08 }}
                  className={`p-3 rounded-xl border-2 ${
                    questionResults[originalQuestionIndex] ? 'border-accent/30 bg-accent/5' : 'border-destructive/30 bg-destructive/5'
                  }`}
                >
                  <div className="flex items-start gap-2">
                    <div className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 mt-0.5 ${
                      questionResults[originalQuestionIndex] ? 'bg-accent text-white' : 'bg-destructive text-white'
                    }`}>
                      {questionResults[originalQuestionIndex] ? <Check className="w-3 h-3" strokeWidth={3} /> : <span className="text-[10px] font-bold">✕</span>}
                    </div>
                    <div>
                      <p className="text-sm font-medium mb-1">Q{i + 1}: {stripHtml(q.question)}</p>
                      <p className="text-xs text-muted-foreground">
                        Your answer: {displayStudentAnswer}
                        {!questionResults[i] && ` | Correct: ${displayCorrectAnswer}`}
                      </p>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`w-full ${className}`}>
      <div className="bg-gradient-to-br from-amber-500/5 via-orange-500/5 to-rose-500/5 rounded-3xl p-5 sm:p-6 border border-border/40">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-2 bg-amber-500/10 text-amber-600 px-4 py-1.5 rounded-full mb-3">
            <Flame className="w-4 h-4" />
            <span className="text-sm font-bold">Time to Climb!</span>
          </div>
          <p className="text-xs text-muted-foreground">
            Answer before time runs out!
          </p>
          <div className="flex flex-col items-center justify-center gap-2 mt-3 text-sm text-muted-foreground sm:flex-row sm:gap-4">
            <div className="flex items-center gap-1">
              <Zap className="w-4 h-4 text-primary" />
              <span className="font-medium">Question {currentQuestionIndex + 1} of {totalQuestions}</span>
            </div>
            {totalQuestions > 1 && (
              <div className="flex gap-1.5">
                {questionOrder.map((originalQuestionIndex, i) => (
                  <div key={i} className={`w-2.5 h-2.5 rounded-full transition-all ${
                    i < currentQuestionIndex ? (questionResults[originalQuestionIndex] ? 'bg-accent' : 'bg-destructive')
                    : i === currentQuestionIndex ? 'bg-primary scale-125'
                    : 'bg-muted-foreground/20'
                  }`} />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Timer — enhanced ring */}
        <div className="flex items-center justify-center mb-6">
          <div className="relative">
            <svg className="w-24 h-24 transform -rotate-90">
              <circle cx="48" cy="48" r="42" stroke="currentColor" strokeWidth="5" fill="none" className="text-muted/30" />
              <motion.circle
                cx="48" cy="48" r="42"
                stroke="currentColor"
                strokeWidth="5" fill="none" strokeLinecap="round"
                className={timeLeft <= 5 ? 'text-destructive' : 'text-amber-500'}
                initial={{ pathLength: 1 }}
                animate={{ pathLength: timeLeft / timeLimit }}
                style={{ strokeDasharray: '264', strokeDashoffset: 0 }}
              />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center">
                <div className={`text-2xl font-extrabold ${timeLeft <= 5 ? 'text-destructive animate-pulse' : 'text-foreground'}`}>{timeLeft}</div>
                <div className="text-[10px] text-muted-foreground uppercase tracking-wide">
                  {timeLimitMode === 'all_questions' ? 'total' : 'sec'}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Question */}
        <h2 className="text-xl md:text-2xl font-bold text-center mb-6">{stripHtml(currentQuestion.question)}</h2>

        {/* Identification Input */}
        {isIdentification ? (
          <div className="max-w-lg mx-auto">
            <Input
              value={identificationInput}
              onChange={(e) => !showResult && setIdentificationInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && !showResult && identificationInput.trim() && handleIdentificationSubmit()}
              disabled={showResult}
              placeholder="Type your answer here..."
              className="text-center text-lg rounded-2xl p-5 h-auto"
              autoFocus
            />
            {!showResult && (
              <Button
                onClick={handleIdentificationSubmit}
                disabled={!identificationInput.trim()}
                className="w-full mt-3 rounded-2xl h-12 text-base"
              >
                Submit Answer
              </Button>
            )}
          </div>
        ) : (
          /* Multiple Choice Options — color-coded cards */
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-2xl mx-auto min-w-0">
            {currentQuestion.options.map((option, index) => {
              const color = CLIMB_COLORS[index % CLIMB_COLORS.length];
              return (
                <motion.button
                  key={index}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  whileHover={{ scale: showResult ? 1 : 1.03 }}
                  whileTap={{ scale: showResult ? 1 : 0.97 }}
                  onClick={() => !showResult && handleAnswer(index)}
                  disabled={showResult}
                  className={`min-w-0 p-4 sm:p-5 rounded-2xl text-left font-medium transition-all overflow-hidden ${
                    showResult && index === currentQuestion.correctAnswer
                      ? 'bg-accent text-white shadow-lg shadow-accent/30 ring-2 ring-accent/30'
                      : showResult && index === currentSelection && index !== currentQuestion.correctAnswer
                      ? 'bg-destructive text-white shadow-lg'
                      : currentSelection === index
                      ? `${color.bg} text-white shadow-md`
                      : `bg-card border-2 border-border/50 hover:${color.border} hover:shadow-md`
                  }`}
                >
                  <div className="flex min-w-0 items-center gap-3">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-extrabold text-sm shrink-0 ${
                      showResult && index === currentQuestion.correctAnswer
                        ? 'bg-white/20 text-white'
                        : currentSelection === index
                        ? 'bg-white/20 text-white'
                        : `bg-muted/80 ${color.text}`
                    }`}>
                      {String.fromCharCode(65 + index)}
                    </div>
                    <span className="min-w-0 break-words text-sm">{option}</span>
                  </div>
                </motion.button>
              );
            })}
          </div>
        )}

        {/* Result Message — enhanced */}
        {showResult && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            className={`mt-5 p-4 rounded-2xl text-center border ${
              isIdentification
                ? (questionResults[currentOriginalQuestionIndex] ? 'bg-accent/10 text-accent border-accent/20' : 'bg-destructive/10 text-destructive border-destructive/20')
                : (currentSelection === currentQuestion.correctAnswer
                    ? 'bg-accent/10 text-accent border-accent/20'
                    : 'bg-destructive/10 text-destructive border-destructive/20')
            }`}
          >
            <p className="break-words font-bold text-base">
              {isIdentification
                ? (questionResults[currentOriginalQuestionIndex]
                    ? '✅ Correct! Climbing up!'
                    : `❌ The answer is: ${currentQuestion.correctAnswer}`)
                : (currentSelection === currentQuestion.correctAnswer
                    ? '✅ Correct! Climbing up!'
                    : `❌ The answer is: ${currentQuestion.options[currentQuestion.correctAnswer as number]}`)}
            </p>
            {currentQuestionIndex < totalQuestions - 1 && (
              <p className="text-sm mt-1 flex items-center justify-center gap-1">
                Next question incoming <ChevronRight className="w-4 h-4" />
              </p>
            )}
          </motion.div>
        )}
      </div>
    </div>
  );
}
